export type FounderSubmenu = {
  id: string;
  label: string;
  path: string;
  supportsEdit?: boolean;
  cardAction?: "View" | "Manage" | "Edit";
};

export type FounderNavSection = {
  id: string;
  label: string;
  basePath: string;
  submenus: FounderSubmenu[];
};

export const FOUNDER_HUB_PATH = "/founder/hub";
export const FOUNDER_HOME_PATH = FOUNDER_HUB_PATH;

export const FOUNDER_NAV_SECTIONS: FounderNavSection[] = [
  {
    id: "overview",
    label: "Overview",
    basePath: "/founder/overview",
    submenus: [
      { id: "dashboard", label: "Dashboard", path: "/founder/overview/dashboard", cardAction: "View" },
      { id: "activity", label: "Recent Activity", path: "/founder/overview/activity", cardAction: "View" },
    ],
  },
  {
    id: "traffic",
    label: "Website Traffic",
    basePath: "/founder/traffic",
    submenus: [
      { id: "analytics", label: "Traffic", path: "/founder/traffic/analytics", cardAction: "View" },
    ],
  },
  {
    id: "apps",
    label: "Apps & Reviews",
    basePath: "/founder/apps",
    submenus: [
      { id: "pending", label: "Pending Reviews", path: "/founder/apps/pending", cardAction: "Manage" },
      { id: "published", label: "Published Apps", path: "/founder/apps/published", cardAction: "View" },
      { id: "rejected", label: "Rejected Apps", path: "/founder/apps/rejected", cardAction: "View" },
      { id: "all", label: "All Apps", path: "/founder/apps/all", cardAction: "View" },
    ],
  },
  {
    id: "publishers",
    label: "Publishers",
    basePath: "/founder/publishers",
    submenus: [
      { id: "list", label: "Publisher List", path: "/founder/publishers/list", cardAction: "Manage" },
    ],
  },
  {
    id: "memberships",
    label: "Memberships & Payments",
    basePath: "/founder/memberships",
    submenus: [
      { id: "active", label: "Active Memberships", path: "/founder/memberships/active", cardAction: "Manage" },
      {
        id: "plans",
        label: "Membership Plans",
        path: "/founder/memberships/plans",
        supportsEdit: true,
        cardAction: "Edit",
      },
      { id: "payments", label: "Payments & Renewals", path: "/founder/memberships/payments", cardAction: "View" },
    ],
  },
  {
    id: "marketplace",
    label: "Marketplace",
    basePath: "/founder/marketplace",
    submenus: [
      { id: "categories", label: "Categories", path: "/founder/marketplace/categories", cardAction: "Manage" },
    ],
  },
  {
    id: "platform",
    label: "Platform Control",
    basePath: "/founder/platform",
    submenus: [
      { id: "health", label: "System Health", path: "/founder/platform/health", cardAction: "View" },
      { id: "security", label: "Platform Security", path: "/founder/platform/security", cardAction: "View" },
      {
        id: "publishing",
        label: "Publishing Rules",
        path: "/founder/platform/publishing",
        supportsEdit: true,
        cardAction: "Edit",
      },
      { id: "legal", label: "Legal Documents", path: "/founder/platform/legal", cardAction: "View" },
      { id: "audit", label: "Audit Log", path: "/founder/platform/audit", cardAction: "View" },
    ],
  },
  {
    id: "account",
    label: "My Account",
    basePath: "/founder/account",
    submenus: [
      { id: "profile", label: "My Profile", path: "/founder/account/profile", cardAction: "View" },
      { id: "login-security", label: "Login & Security", path: "/founder/account/security", cardAction: "Manage" },
    ],
  },
];

export function buildFounderSectionHubPath(sectionId: string): string {
  return `${FOUNDER_HUB_PATH}/${sectionId}`;
}

export function isFounderHubPath(pathname: string): boolean {
  return pathname === FOUNDER_HUB_PATH || pathname.startsWith(`${FOUNDER_HUB_PATH}/`);
}

export function resolveFounderHubSectionId(pathname: string): string | null {
  if (pathname === FOUNDER_HUB_PATH) {
    return null;
  }

  const match = pathname.match(/^\/founder\/hub\/([^/]+)$/);
  if (!match) {
    return null;
  }

  const section = FOUNDER_NAV_SECTIONS.find((item) => item.id === match[1]);
  return section?.id ?? null;
}

export function resolveFounderSection(pathname: string): FounderNavSection | null {
  if (isFounderHubPath(pathname)) {
    const hubSectionId = resolveFounderHubSectionId(pathname);
    return FOUNDER_NAV_SECTIONS.find((section) => section.id === hubSectionId) ?? null;
  }

  return (
    FOUNDER_NAV_SECTIONS.find(
      (section) =>
        pathname === section.basePath || pathname.startsWith(`${section.basePath}/`),
    ) ?? null
  );
}

export function resolveFounderSubmenu(
  pathname: string,
  section: FounderNavSection,
): FounderSubmenu | null {
  const exact = section.submenus.find((item) => item.path === pathname);
  if (exact) {
    return exact;
  }

  return (
    section.submenus.find((item) => pathname.startsWith(`${item.path}/`)) ?? null
  );
}

export function resolveFounderSectionIdFromPath(pathname: string): string | null {
  if (isFounderHubPath(pathname)) {
    return resolveFounderHubSectionId(pathname);
  }

  return resolveFounderSection(pathname)?.id ?? null;
}

export function buildFounderBreadcrumbLabels(pathname: string): string[] {
  const section = resolveFounderSection(pathname);
  if (!section) {
    return ["Founder Control Centre"];
  }

  if (isFounderHubPath(pathname)) {
    return [section.label];
  }

  const submenu = resolveFounderSubmenu(pathname, section);
  const labels = [section.label];
  if (submenu) {
    labels.push(submenu.label);
  }

  if (pathname.includes("/publishers/list/")) {
    labels.push("Publisher");
  }

  if (/^\/founder\/apps\/[^/]+\/[^/]+$/.test(pathname)) {
    labels.push("App Detail");
  }

  return labels;
}

export function getFounderSubmenuGridClass(count: number): string {
  if (count <= 1) {
    return "oac-founder-submenu-grid--1";
  }

  if (count === 2) {
    return "oac-founder-submenu-grid--2";
  }

  if (count === 3) {
    return "oac-founder-submenu-grid--3";
  }

  if (count === 4) {
    return "oac-founder-submenu-grid--4";
  }

  return "oac-founder-submenu-grid--scroll";
}
